import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Users, Copy, Share2, Mail, MessageCircle, Link2, Check, Smartphone, QrCode, Download } from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import { useEffect } from 'react';

interface CollaborativePackingInviteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tripId?: string;
  destination: string;
  shareCode: string;
}

export function CollaborativePackingInvite({
  open,
  onOpenChange,
  tripId,
  destination,
  shareCode
}: CollaborativePackingInviteProps) {
  const [emails, setEmails] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  const inviteLink = `https://odyvoy.com/join/${shareCode}`;
  const appStoreLink = 'https://apps.apple.com/app/odyvoy';
  const playStoreLink = 'https://play.google.com/store/apps/details?id=app.lovable.odyvoy';

  useEffect(() => {
    if (open && shareCode) {
      QRCode.toDataURL(inviteLink, {
        width: 200,
        margin: 2,
        color: {
          dark: '#18181b',
          light: '#ffffff'
        }
      }).then(setQrCodeUrl);
    }
  }, [open, shareCode, inviteLink]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setIsCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(shareCode);
      toast.success('Code copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const shareViaWhatsApp = () => {
    const message = encodeURIComponent(
      `🧳 Join my packing list for ${destination}!\n\n` +
      `I'm using OdyVoy to pack smart for our trip. Join with code: ${shareCode}\n\n` +
      `Open this link to join: ${inviteLink}\n\n` +
      `Let's pack together! ✈️`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Join my packing list for ${destination}`);
    const body = encodeURIComponent(
      `Hey!\n\n` +
      `I'm using OdyVoy to pack for our trip to ${destination}. ` +
      `It's an AI-powered travel companion that makes packing so much easier!\n\n` +
      `Join my packing list:\n` +
      `1. Download OdyVoy: ${inviteLink}\n` +
      `2. Enter code: ${shareCode}\n\n` +
      `We can collaborate on what to pack and track our progress together.\n\n` +
      `See you soon! ✈️`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaSMS = () => {
    const message = encodeURIComponent(
      `Join my packing list for ${destination}! Download OdyVoy and use code: ${shareCode} - ${inviteLink}`
    );
    window.location.href = `sms:?body=${message}`;
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join my packing list for ${destination}`,
          text: `I'm using OdyVoy to pack for our trip. Join with code: ${shareCode}`,
          url: inviteLink
        });
      } catch {
        // User cancelled
      }
    } else {
      copyLink();
    }
  };

  const downloadQR = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.download = `odyvoy-invite-${shareCode}.png`;
      link.href = qrCodeUrl;
      link.click();
      toast.success('QR code downloaded!');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Invite Travel Buddies
          </DialogTitle>
          <DialogDescription>
            Share your packing list with friends and pack together
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Share Code Card */}
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-4 text-center space-y-3">
              <p className="text-sm text-muted-foreground">Share Code</p>
              <div className="flex items-center justify-center gap-2">
                <span className="text-3xl font-mono font-bold tracking-widest text-primary">
                  {shareCode}
                </span>
                <Button variant="ghost" size="sm" onClick={copyCode}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <Badge variant="secondary" className="text-xs">
                Trip to {destination}
              </Badge>
            </CardContent>
          </Card>

          {/* QR Code */}
          {qrCodeUrl && (
            <div className="flex flex-col items-center gap-2 p-4 border rounded-lg bg-white">
              <img src={qrCodeUrl} alt="QR Code" className="w-32 h-32" />
              <p className="text-xs text-muted-foreground text-center">
                Scan to join packing list
              </p>
              <Button variant="ghost" size="sm" onClick={downloadQR} className="text-xs">
                <Download className="h-3 w-3 mr-1" />
                Download QR
              </Button>
            </div>
          )}

          {/* Copy Link */}
          <div className="space-y-2">
            <Label className="text-sm">Invite Link</Label>
            <div className="flex gap-2">
              <Input 
                value={inviteLink} 
                readOnly 
                className="font-mono text-sm"
              />
              <Button onClick={copyLink} variant="outline" size="icon">
                {isCopied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={shareViaWhatsApp} variant="outline" className="gap-2">
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </Button>
            <Button onClick={shareViaEmail} variant="outline" className="gap-2">
              <Mail className="h-4 w-4" />
              Email
            </Button>
            <Button onClick={shareViaSMS} variant="outline" className="gap-2">
              <Smartphone className="h-4 w-4" />
              SMS
            </Button>
            <Button onClick={shareNative} className="gap-2">
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          </div>

          {/* App Download CTA */}
          <Card className="border-dashed">
            <CardContent className="p-4 text-center space-y-2">
              <p className="text-sm font-medium">Works in any browser</p>
              <p className="text-xs text-muted-foreground">
                Friends open your link, create a free account and edit this exact list with you — changes sync live.
              </p>
              <div className="flex justify-center gap-2 pt-2">
                <Badge variant="outline" className="text-xs">
                  <Link2 className="h-3 w-3 mr-1" />
                  Free Download
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="ghost" className="w-full">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
